/** Rashis and nakshatras for the 108-charan wheel (0° Mesha at top, clockwise). */

export const NAKSHATRA_WHEEL_RASHIS = [
  { n: 1, sa: 'Mesha', en: 'Aries' },
  { n: 2, sa: 'Vrishabha', en: 'Taurus' },
  { n: 3, sa: 'Mithuna', en: 'Gemini' },
  { n: 4, sa: 'Karka', en: 'Cancer' },
  { n: 5, sa: 'Simha', en: 'Leo' },
  { n: 6, sa: 'Kanya', en: 'Virgo' },
  { n: 7, sa: 'Tula', en: 'Libra' },
  { n: 8, sa: 'Vrishchika', en: 'Scorpio' },
  { n: 9, sa: 'Dhanu', en: 'Sagittarius' },
  { n: 10, sa: 'Makara', en: 'Capricorn' },
  { n: 11, sa: 'Kumbha', en: 'Aquarius' },
  { n: 12, sa: 'Mina', en: 'Pisces' },
];

/** English tropical sign → Hindi (Devanagari) for teaching UI. */
export const RASHI_EN_TO_HI = {
  Aries: 'मेष',
  Taurus: 'वृषभ',
  Gemini: 'मिथुन',
  Cancer: 'कर्क',
  Leo: 'सिंह',
  Virgo: 'कन्या',
  Libra: 'तुला',
  Scorpio: 'वृश्चिक',
  Sagittarius: 'धनु',
  Capricorn: 'मकर',
  Aquarius: 'कुंभ',
  Pisces: 'मीन',
};

const _RASHI_EN_REPLACE_ORDER = [
  'Sagittarius',
  'Capricorn',
  'Aquarius',
  'Pisces',
  'Scorpio',
  'Gemini',
  'Libra',
  'Taurus',
  'Cancer',
  'Virgo',
  'Leo',
  'Aries',
];

/** Swaps English sign words in a phrase (e.g. degree bands) for Hindi labels. */
export function rashisEnglishToHindi(s) {
  if (!s) return '';
  let out = s;
  for (const en of _RASHI_EN_REPLACE_ORDER) {
    out = out.split(en).join(RASHI_EN_TO_HI[en]);
  }
  return out;
}

export const NAKSHATRA_WHEEL_NAMES = [
  'Ashwini',
  'Bharani',
  'Krittika',
  'Rohini',
  'Mrigashira',
  'Ardra',
  'Punarvasu',
  'Pushya',
  'Ashlesha',
  'Magha',
  'Purva Phalguni',
  'Uttara Phalguni',
  'Hasta',
  'Chitra',
  'Swati',
  'Vishakha',
  'Anuradha',
  'Jyeshtha',
  'Mula',
  'Purva Ashadha',
  'Uttara Ashadha',
  'Shravana',
  'Dhanishtha',
  'Shatabhisha',
  'Purva Bhadrapada',
  'Uttara Bhadrapada',
  'Revati',
];

/** Devanagari names (same order as {@link NAKSHATRA_WHEEL_NAMES}). */
export const NAKSHATRA_WHEEL_NAMES_HI = [
  'अश्विनी',
  'भरणी',
  'कृत्तिका',
  'रोहिणी',
  'मृगशिरा',
  'आर्द्रा',
  'पुनर्वसु',
  'पुष्य',
  'आश्लेषा',
  'मघा',
  'पूर्व फाल्गुनी',
  'उत्तर फाल्गुनी',
  'हस्त',
  'चित्रा',
  'स्वाति',
  'विशाखा',
  'अनुराधा',
  'ज्येष्ठा',
  'मूल',
  'पूर्वाषाढ़ा',
  'उत्तराषाढ़ा',
  'श्रवण',
  'धनिष्ठा',
  'शतभिषा',
  'पूर्व भाद्रपद',
  'उत्तर भाद्रपद',
  'रेवती',
];

/**
 * Soft glow RGB (`r, g, b` comma string) per index 0-26 for UI (e.g. intro mini-cards).
 * Derived from classical **gana**: Deva (gentle/teal tones), Manushya (warm gold), Rakshasa (deep rose).
 * Same 27-order as {@link NAKSHATRA_WHEEL_NAMES}.
 */
const _GANA_RGB = { D: '45, 212, 191', M: '251, 191, 36', R: '225, 29, 72' };
const _NAKSHATRA_GANA_KEY = [
  'D',
  'M',
  'R',
  'M',
  'D',
  'M',
  'D',
  'D',
  'R',
  'R',
  'M',
  'M',
  'D',
  'R',
  'D',
  'R',
  'D',
  'R',
  'R',
  'M',
  'M',
  'D',
  'R',
  'R',
  'M',
  'M',
  'D',
];
export const NAKSHATRA_GANA_GLOW_RGB = _NAKSHATRA_GANA_KEY.map((k) => _GANA_RGB[k]);

/** Charan indices where full radial lines mark water-fire (gandanta) cusps: Mina-Mesha / Revati-Ashwini (0°), Karka-Simha / Ashlesha-Magha (120°), Vrishchika-Dhanu / Jyeshtha-Mula (240°). Same as p=108; we style p=0 only to avoid double-drawing. */
export const GANDANTA_RADIAL_CHARAN_INDICES = new Set([0, 36, 72]);

export const WHEEL_CX = 500;
export const WHEEL_CY = 500;
export const WHEEL_R = {
  hole: 86,
  rashiIn: 92,
  rashiOut: 208,
  charanIn: 212,
  charanOut: 256,
  nakIn: 260,
  nakOut: 392,
};

/** Outer radius for the twelve-rashi ring (expanded on rashi-only wheels). */
export function wheelRashiOuterR(ringMode = 'full') {
  return ringMode === 'rashi' ? WHEEL_R.nakOut : WHEEL_R.rashiOut;
}

export function wheelWedgePath(cx, cy, rIn, rOut, i, n) {
  const a0 = -Math.PI / 2 + (i / n) * 2 * Math.PI;
  const a1 = -Math.PI / 2 + ((i + 1) / n) * 2 * Math.PI;
  const xi0 = cx + rIn * Math.cos(a0);
  const yi0 = cy + rIn * Math.sin(a0);
  const xo0 = cx + rOut * Math.cos(a0);
  const yo0 = cy + rOut * Math.sin(a0);
  const xo1 = cx + rOut * Math.cos(a1);
  const yo1 = cy + rOut * Math.sin(a1);
  const xi1 = cx + rIn * Math.cos(a1);
  const yi1 = cy + rIn * Math.sin(a1);
  return `M ${xi0} ${yi0} L ${xo0} ${yo0} A ${rOut} ${rOut} 0 0 1 ${xo1} ${yo1} L ${xi1} ${yi1} A ${rIn} ${rIn} 0 0 0 ${xi0} ${yi0} Z`;
}

export function wheelRadialLine(cx, cy, r0, r1, i, n) {
  const a = -Math.PI / 2 + (i / n) * 2 * Math.PI;
  return {
    x0: cx + r0 * Math.cos(a),
    y0: cy + r0 * Math.sin(a),
    x1: cx + r1 * Math.cos(a),
    y1: cy + r1 * Math.sin(a),
  };
}

export function wheelTextRotate(midAngleRad) {
  const deg = (midAngleRad * 180) / Math.PI;
  let rot = deg + 90;
  if (deg > 90 || deg < -90) rot += 180;
  return rot;
}

/** Rashi indices (0-11) whose 30° band overlaps nakshatra k’s 13°20′ band on the shared wheel. */
export function rashisOverlappingNakshatra(k) {
  const nakStart = (k / 27) * 360;
  const nakEnd = ((k + 1) / 27) * 360;
  const out = [];
  for (let r = 0; r < 12; r++) {
    const rs = (r / 12) * 360;
    const re = ((r + 1) / 12) * 360;
    if (nakStart < re && nakEnd > rs) out.push(r);
  }
  return out;
}

/** Nakshatra indices (0-26) whose band overlaps rashi r’s 30° band. */
export function nakshatrasOverlappingRashi(r) {
  const out = [];
  for (let k = 0; k < 27; k++) {
    if (rashisOverlappingNakshatra(k).includes(r)) out.push(k);
  }
  return out;
}

/** Midpoint of wedge i of n between radii rIn and rOut (0° Mesha at top, clockwise). */
export function wheelWedgeMidpoint(cx, cy, rIn, rOut, i, n) {
  const mid = -Math.PI / 2 + ((i + 0.5) / n) * 2 * Math.PI;
  const r = (rIn + rOut) / 2;
  return { x: cx + r * Math.cos(mid), y: cy + r * Math.sin(mid), mid };
}

/** Short label for tight SVG ring */
export function shortNakshatraLabel(name) {
  if (name.length <= 13) return name;
  return name.replace('Purva ', 'P. ').replace('Uttara ', 'U. ');
}

const _DEVA_DIGITS = '०१२३४५६७८९';

/** @param {number | string} n */
export function toDevanagariDigits(n) {
  return String(n).replace(/\d/g, (d) => _DEVA_DIGITS[Number(d)]);
}

/** Format decimal degrees as d°m′ (optionally Devanagari numerals). */
export function formatWheelDegrees(deg, hindi = false) {
  const totalMin = Math.round(deg * 60);
  const d = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  const ds = hindi ? toDevanagariDigits(d) : String(d);
  if (m === 0) return `${ds}°`;
  const ms = hindi ? toDevanagariDigits(m).padStart(2, '०') : String(m).padStart(2, '0');
  return `${ds}°${ms}′`;
}

function _signNameAtLongitude(absDeg, showEnglish) {
  const idx = Math.floor(absDeg / 30) % 12;
  const row = NAKSHATRA_WHEEL_RASHIS[idx];
  if (showEnglish) return row.en;
  return RASHI_EN_TO_HI[row.en] ?? row.sa;
}

function _longitudePointLabel(absDeg, showEnglish) {
  const sign = _signNameAtLongitude(absDeg, showEnglish);
  return `${sign} ${formatWheelDegrees(absDeg % 30, !showEnglish)}`;
}

/** Sign-relative degree band for a wheel sector i of n (0° Mesha at top). */
export function wheelSectorDegreeBand(i, n, showEnglish) {
  const start = (i / n) * 360;
  const end = ((i + 1) / n) * 360;
  const hi = !showEnglish;
  // Twelve rashis: absolute ecliptic arc from 0° Mesha (Mesha 0–30°, Vrishabha 30–60°, …).
  if (n === 12) {
    return `${formatWheelDegrees(start, hi)} – ${formatWheelDegrees(end, hi)}`;
  }
  if (end >= 360 - 1e-9) {
    return `${_longitudePointLabel(start, showEnglish)} – ${_signNameAtLongitude(0, showEnglish)} ${formatWheelDegrees(0, !showEnglish)}`;
  }
  const startSign = Math.floor(start / 30);
  const endSign = Math.floor((end - 1e-9) / 30);
  if (startSign === endSign) {
    const sign = _signNameAtLongitude(start, showEnglish);
    const hi = !showEnglish;
    return `${sign} ${formatWheelDegrees(start % 30, hi)} – ${formatWheelDegrees(end % 30, hi)}`;
  }
  return `${_longitudePointLabel(start, showEnglish)} – ${_longitudePointLabel(end, showEnglish)}`;
}

/** Ecliptic arc from 0° Mesha (absolute). */
export function wheelSectorMeshaBand(i, n, showEnglish) {
  const hi = !showEnglish;
  const start = (i / n) * 360;
  const end = ((i + 1) / n) * 360;
  const a = formatWheelDegrees(start, hi);
  const b = formatWheelDegrees(end >= 360 ? 360 : end, hi);
  return showEnglish ? `0° Aries → ${a} – ${b}` : `०° मेष → ${a} – ${b}`;
}

export function nakshatraIndexForCharan(p) {
  return Math.floor(p / 4);
}

export function charanNumberInNakshatra(p) {
  return (p % 4) + 1;
}

/** Rashi wheel indices (0–11) to emphasize in flank cards for a wheel hover target. */
export function wheelHighlightRashiIndicesFromHover(hover) {
  if (!hover) return [];
  if (hover.kind === 'rashi') return [hover.index];
  if (hover.kind === 'nak') return rashisOverlappingNakshatra(hover.index);
  if (hover.kind === 'charan') return rashisOverlappingNakshatra(nakshatraIndexForCharan(hover.index));
  return [];
}

export function wheelSectorSpanLabel(kind, showEnglish) {
  if (showEnglish) {
    if (kind === 'rashi') return '30° per rashi';
    if (kind === 'nakshatra') return '13° 20′ per nakshatra';
    return '3° 20′ per charan (pada)';
  }
  if (kind === 'rashi') return 'प्रति राशि ३०°';
  if (kind === 'nakshatra') return 'प्रति नक्षत्र १३° २०′';
  return 'प्रति चरण ३° २०′ (पद)';
}
